import type { Player } from "./player";
import type { Weapon } from "./weapon";
import { deserializeWeapon } from "./serialization";
import { getFamilyForWeapon, getOrderInFamily } from "./weaponFamilyConfig";

export class Arsenal {
	weapons: Weapon[] = [];
	families: Map<number, Weapon[]> = new Map();
	selectedFamilyId: number = 0;
	selectedIndexInFamily: Map<number, number> = new Map();

	addWeapon(weapon: Weapon): void {
		for (const existing of this.weapons) {
			if (existing.constructor !== weapon.constructor) continue;
			if (!existing.infinite) existing.ammo += weapon.ammo;
			return;
		}

		this.weapons.push(weapon);

		const familyId = getFamilyForWeapon(weapon);
		if (!this.families.has(familyId)) {
			this.families.set(familyId, []);
			this.selectedIndexInFamily.set(familyId, 0);
		}
		const familyList = this.families.get(familyId)!;
		const order = getOrderInFamily(weapon);
		let insertAt = familyList.length;
		for (let i = 0; i < familyList.length; i++) {
			if (getOrderInFamily(familyList[i]) > order) {
				insertAt = i;
				break;
			}
		}
		familyList.splice(insertAt, 0, weapon);

		if (this.weapons.length === 1) {
			this.selectedFamilyId = familyId;
		}
	}

	removeWeapon(weapon: Weapon): void {
		const idx = this.weapons.indexOf(weapon);
		if (idx === -1) return;
		this.weapons.splice(idx, 1);

		const familyId = getFamilyForWeapon(weapon);
		const familyList = this.families.get(familyId);
		if (familyList) {
			const fIdx = familyList.indexOf(weapon);
			if (fIdx !== -1) {
				familyList.splice(fIdx, 1);
				const currentIndex = this.selectedIndexInFamily.get(familyId) ?? 0;
				if (familyList.length === 0) {
					this.selectedIndexInFamily.set(familyId, 0);
				} else if (currentIndex >= familyList.length) {
					this.selectedIndexInFamily.set(familyId, familyList.length - 1);
				}
			}
		}

		if (this.selectedFamilyId === familyId && (!familyList || familyList.length === 0)) {
			const activeFamilies = this.getActiveFamilyIds();
			if (activeFamilies.length > 0) {
				this.selectedFamilyId = activeFamilies[0];
			}
		}
	}

	getSelectedWeapon(): Weapon | null {
		const familyList = this.families.get(this.selectedFamilyId);
		if (!familyList || familyList.length === 0) return null;
		const index = this.selectedIndexInFamily.get(this.selectedFamilyId) ?? 0;
		return familyList[index] ?? null;
	}

	selectFamily(familyId: number): void {
		const familyList = this.families.get(familyId);
		if (!familyList || familyList.length === 0) return;

		if (familyId === this.selectedFamilyId) {
			const currentIndex = this.selectedIndexInFamily.get(familyId) ?? 0;
			this.selectedIndexInFamily.set(familyId, (currentIndex + 1) % familyList.length);
		} else {
			this.selectedFamilyId = familyId;
		}
	}

	selectWeaponByIndex(flatIndex: number): void {
		if (flatIndex < 0 || flatIndex >= this.weapons.length) return;
		const weapon = this.weapons[flatIndex];
		const familyId = getFamilyForWeapon(weapon);
		this.selectedFamilyId = familyId;

		const familyList = this.families.get(familyId);
		if (familyList) {
			const indexInFamily = familyList.indexOf(weapon);
			if (indexInFamily !== -1) {
				this.selectedIndexInFamily.set(familyId, indexInFamily);
			}
		}
	}

	selectNextWeapon(): void {
		if (this.weapons.length === 0) return;
		const current = this.getSelectedWeapon();
		if (!current) return;
		const currentFlatIndex = this.weapons.indexOf(current);
		const nextIndex = Math.min(currentFlatIndex + 1, this.weapons.length - 1);
		this.selectWeaponByIndex(nextIndex);
	}

	selectPreviousWeapon(): void {
		if (this.weapons.length === 0) return;
		const current = this.getSelectedWeapon();
		if (!current) return;
		const currentFlatIndex = this.weapons.indexOf(current);
		const prevIndex = Math.max(currentFlatIndex - 1, 0);
		this.selectWeaponByIndex(prevIndex);
	}

	getAllWeapons(): Weapon[] {
		return [...this.weapons];
	}

	getFamilyWeapons(familyId: number): Weapon[] {
		return this.families.get(familyId) ?? [];
	}

	getActiveFamilyIds(): number[] {
		const ids: number[] = [];
		for (const [familyId, weapons] of this.families) {
			if (weapons.length > 0) ids.push(familyId);
		}
		return ids.sort((a, b) => a - b);
	}

	serialize(): Record<string, unknown> {
		const selectedIndexObj: Record<number, number> = {};
		for (const [familyId, index] of this.selectedIndexInFamily) {
			selectedIndexObj[familyId] = index;
		}
		return {
			weapons: this.weapons.map((w) => w.serialize()),
			selectedFamilyId: this.selectedFamilyId,
			selectedIndexInFamily: selectedIndexObj,
		};
	}

	static deserialize(data: any, owner: Player): Arsenal {
		const arsenal = new Arsenal();

		if (!data.weapons || !Array.isArray(data.weapons)) return arsenal;

		const weapons: Weapon[] = data.weapons.map((w: any) => deserializeWeapon(w, owner));

		for (const weapon of weapons) {
			arsenal.weapons.push(weapon);
			const familyId = getFamilyForWeapon(weapon);
			if (!arsenal.families.has(familyId)) {
				arsenal.families.set(familyId, []);
			}
			const familyList = arsenal.families.get(familyId)!;
			const order = getOrderInFamily(weapon);
			let insertAt = familyList.length;
			for (let i = 0; i < familyList.length; i++) {
				if (getOrderInFamily(familyList[i]) > order) {
					insertAt = i;
					break;
				}
			}
			familyList.splice(insertAt, 0, weapon);
		}

		if (data.selectedFamilyId !== undefined && data.selectedIndexInFamily !== undefined) {
			arsenal.selectedFamilyId = data.selectedFamilyId;
			const indexObj = data.selectedIndexInFamily;
			for (const key of Object.keys(indexObj)) {
				arsenal.selectedIndexInFamily.set(Number(key), indexObj[key]);
			}
		} else {
			// Old save format: derive from flat selectedWeaponIndex
			const flatIndex = data.selectedWeaponIndex ?? 0;
			if (flatIndex >= 0 && flatIndex < arsenal.weapons.length) {
				arsenal.selectWeaponByIndex(flatIndex);
			}
			// Initialize selectedIndexInFamily for all families
			for (const [familyId] of arsenal.families) {
				if (!arsenal.selectedIndexInFamily.has(familyId)) {
					arsenal.selectedIndexInFamily.set(familyId, 0);
				}
			}
		}

		return arsenal;
	}
}
